/**
 * Připojení na živý proud událostí (SSE).
 *
 * Tenhle kanál je záměrně "best effort" — sklad zůstává jediným zdrojem
 * pravdy a snapshot ho každých 30 s přepíše. Když proud vypadne nebo se
 * událost ztratí, objeví se do minuty přes dávkovou cestu. Proto se tu
 * nikde nic neukládá ani neopakuje.
 *
 * Nepoužívá se EventSource, protože ten neumí poslat hlavičku Authorization
 * a klíč nechceme dávat do URL (nginx si ji zapíše do logu). Fetch se
 * streamem to zvládne, jen si musíme sami ohlídat znovupřipojení.
 */

export interface LiveEventPayload {
  event_id?: string
  event_name?: string
  venue_id?: string
  occurred_at?: string
  dims?: Record<string, string>
  [k: string]: unknown
}

const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000

export interface LiveEventsHandle {
  stop(): void
}

export function connectLiveEvents(
  url: string,
  apiKey: string,
  onEvent: (e: LiveEventPayload) => void,
): LiveEventsHandle {
  let stopped = false
  let controller: AbortController | null = null
  let retryMs = RECONNECT_MIN_MS
  let timer: ReturnType<typeof setTimeout> | null = null

  const scheduleReconnect = () => {
    if (stopped) return
    timer = setTimeout(() => void run(), retryMs)
    // Exponenciální backoff, ať se při delším výpadku serveru nemlátíme.
    retryMs = Math.min(retryMs * 2, RECONNECT_MAX_MS)
  }

  const run = async () => {
    if (stopped) return
    controller = new AbortController()
    try {
      const res = await fetch(url, {
        headers: { Authorization: `ApiKey ${apiKey}` },
        signal: controller.signal,
      })
      if (!res.ok || !res.body) {
        scheduleReconnect()
        return
      }

      // Spojení drží, takže backoff můžeme vrátit na začátek.
      retryMs = RECONNECT_MIN_MS

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // Rámce SSE jsou oddělené prázdným řádkem.
        let sep = buffer.indexOf('\n\n')
        while (sep !== -1) {
          const frame = buffer.slice(0, sep)
          buffer = buffer.slice(sep + 2)
          for (const line of frame.split('\n')) {
            // Řádky začínající ':' jsou komentáře (ping) — přeskočit.
            if (!line.startsWith('data:')) continue
            const raw = line.slice(5).trim()
            if (!raw) continue
            try {
              onEvent(JSON.parse(raw) as LiveEventPayload)
            } catch {
              // Nečitelný rámec zahodíme; dávková cesta ho stejně doplní.
            }
          }
          sep = buffer.indexOf('\n\n')
        }
      }
      scheduleReconnect()
    } catch {
      if (!stopped) scheduleReconnect()
    }
  }

  void run()

  return {
    stop() {
      stopped = true
      if (timer) clearTimeout(timer)
      controller?.abort()
    },
  }
}
